import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';


import {AccountLink} from 'src/components/links';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {selectUserInformation} from 'src/modules/profile/user/services/user-profile-slice';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import {CrmSettingComponent} from './crm-setting.component';

import type {Dispatch, SetStateAction} from 'react';
import type {UserCrmModule} from 'src/openapi/admin/models/UserCrmModule';
import type {UserCrmSettings} from 'src/openapi/admin/models/UserCrmSettings';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    crmSettings: UserCrmSettings,
    onCrmSettingsChange: Dispatch<SetStateAction<UserCrmSettings>>,
    disabled?: boolean,
    userId: number | undefined,
    onFieldChange: (fieldName: string) => void,
};

export const UserCrmSettingsSection = (props: Props) => {
    const usersAccountSelector = useSelector(selectUserInformation)?.account;
    const accountSelector = useSelector(selectAccountDetails).hierarchy?.current;
    const account = usersAccountSelector
        || (accountSelector && {accountId: accountSelector.accountId || 0, accountName: accountSelector.name || ''})
        || null;

    const {
        isLoading,
        crmSettings,
        onCrmSettingsChange,
        disabled,
        userId,
        onFieldChange,
    } = props;

    const changeCrmModule = useCallback((crmModule: UserCrmModule) => {
        onCrmSettingsChange(prevState => ({
            ...prevState,
            crmModules: crmSettings.crmModules.map(prevModule => (prevModule.crmType === crmModule.crmType
                ? crmModule
                : prevModule
            )),
        }));
    }, [crmSettings, onCrmSettingsChange]);

    const changeSyncSettings = useCallback((changeData: CheckBoxChangeData) => {
        onFieldChange('Disable sync from platform');
        onCrmSettingsChange(prevState => ({
            ...prevState,
            syncFromPlatformDisabled: changeData.event.currentTarget.checked,
        }));
    }, [onCrmSettingsChange, onFieldChange]);

    const changeCreationSettings = useCallback((changeData: CheckBoxChangeData) => {
        onFieldChange('Disable creation from platform');
        onCrmSettingsChange(prevState => ({
            ...prevState,
            disableCreationFromPlatform: changeData.event.currentTarget.checked,
        }));
    }, [onCrmSettingsChange, onFieldChange]);

    const isUserDefined = userId !== undefined;
    const allCrmModulesDisabled = isUserDefined && crmSettings.crmModules.every(module => !module.enabled);

    const crmIntegrationMessage = crmSettings.crmModules.length && !allCrmModulesDisabled ? (
        <Group gap="s" alignItems="start" direction="v">
            <p>
                Configure this user's CRM IDs for {account ? <AccountLink {...account} place="License & Features: CRM Tab"/> : null} CRM integrations.
                Set up additional CRM integrations in the Account Profile.
            </p>
            <Group>
                <CheckBox
                    name="syncFromPlatformDisabled"
                    disabled={disabled || (isUserDefined && allCrmModulesDisabled)}
                    checked={crmSettings.syncFromPlatformDisabled}
                    label="Disable sync from platform"
                    onCheckBoxChange={changeSyncSettings}
                />
                <CheckBox
                    name="creationFromPlatformDisabled"
                    disabled={disabled || (isUserDefined && allCrmModulesDisabled)}
                    checked={crmSettings.disableCreationFromPlatform}
                    label="Disable creation from platform"
                    onCheckBoxChange={changeCreationSettings}
                />
            </Group>
        </Group>
    ) : (
        <p>
            This user's Account does not have any enabled CRM integrations. Set up CRM integrations
            for {account ? (<span style={{display: 'inline-block', maxWidth: 200}}><AccountLink {...account} place="License & Features: CRM Tab"/></span>) : null} account to enable user settings.
        </p>
    );

    return (
        <Skeleton shown={isLoading} view={<div/>}>
            <FormSection headline="CRM User Accounts">
                <Group direction="v" alignItems="start">
                    {crmIntegrationMessage}
                    <ResponsiveElementGroup>
                        {crmSettings.crmModules
                            .map(crmModule => (
                                <CrmSettingComponent
                                    key={crmModule.crmType}
                                    crmModule={crmModule}
                                    onChange={changeCrmModule}
                                    disabled={disabled || (isUserDefined && !crmModule.enabled)}
                                    onFieldChange={onFieldChange}
                                />
                            ))}
                    </ResponsiveElementGroup>
                </Group>
            </FormSection>
        </Skeleton>
    );
};
